import {
	Avatar,
	Center,
	createStyles,
	Group,
	Menu,
	Stack,
	Tooltip,
	UnstyledButton,
} from '@mantine/core';
import { useGetPersonas } from '@repo/api-codegen';
import { useCallback, useMemo } from 'react';

import { AIAvatar } from '@repo/common/components/AI/Avatar';
import { KeyboardShortcut } from '@repo/common/components/KeyboardShortcut/KeyboardShortcut';
import { Button, Icon, IconButton, Text } from '@repo/foundations';
import { MOD_KEY } from '@repo/secoda-editor/utils/keyboard';
import { useMount } from 'ahooks';
import { isEmpty, isNil, size } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import {
	NavBarBreadcrumbWrapper,
	NavBarRightSideWrapper,
	NavBarSidesheetControlsWrapper,
	NavBarWrapper,
} from '.';
import { useActiveAiPersona } from '../../../api/hooks/ai/useActiveAiPersona';
import { useFeatureFlags } from '../../../utils/featureFlags';
import { deleteParamsFromUrl } from '../../../utils/url';
import AIShareButton from '../../AIAssistant/AIShareButton';
import { rightSidebarStore as defaultRightSidebarStore } from '../../RightSidebar';
import type { RightSidebarStore } from '../../RightSidebar/store';

const useStyles = createStyles((theme) => ({
	menuTarget: {
		display: 'flex',
		alignItems: 'center',
		padding: `${theme.spacing['3xs']} calc(${theme.spacing.xs} - 4px) ${theme.spacing['3xs']} calc(${theme.spacing.xs} - 2px)`,
		borderRadius: theme.radius.sm,

		'&:hover': {
			backgroundColor: theme.other.getColor('surface/secondary/default'),
		},

		'&:active, &:focus': {
			backgroundColor: theme.other.getColor('surface/secondary/active'),
		},
	},
}));

function AIPageNavBar({
	rightSidebarStore = defaultRightSidebarStore,
	forceHideRightSidebarControl = false,
}: {
	rightSidebarStore?: RightSidebarStore;
	forceHideRightSidebarControl?: boolean;
}) {
	const { classes, theme } = useStyles();
	const navigate = useNavigate();
	const { id } = useParams();
	const [searchParams] = useSearchParams();

	const { aiPersonas } = useFeatureFlags();

	const { collapsed, setCollapsed } = rightSidebarStore;
	const { activeAiPersonaId, setActiveAiPersonaId } = useActiveAiPersona();

	const { data: personas } = useGetPersonas({});

	// Sort personas to put the default one at the top
	const sortedPersonas = useMemo(() => {
		if (!personas) return [];
		return [...personas].sort((a, b) => {
			if (a.is_default && !b.is_default) return -1;
			if (!a.is_default && b.is_default) return 1;
			return 0;
		});
	}, [personas]);

	const defaultPersona = useMemo(
		() => sortedPersonas?.find((p) => p.is_default),
		[sortedPersonas]
	);

	const activePersona = useMemo(() => {
		// If an active persona is set, use it
		if (activeAiPersonaId) {
			return personas?.find((p) => p.id === activeAiPersonaId);
		}
		// Otherwise, fall back to default persona
		return defaultPersona;
	}, [personas, activeAiPersonaId, defaultPersona]);

	useMount(() => {
		const queryPersonaId = searchParams.get('persona');
		if (queryPersonaId) {
			setActiveAiPersonaId(queryPersonaId);
			deleteParamsFromUrl('persona');
		}
	});

	const handleNewConversation = useCallback(() => {
		navigate('/ai/new');
	}, [navigate]);

	const hasPersonas = size(personas) > 0;

	return (
		<NavBarWrapper>
			<NavBarBreadcrumbWrapper>
				{aiPersonas && (
					<Menu position="bottom-start">
						<Menu.Target>
							<UnstyledButton
								disabled={!hasPersonas}
								className={classes.menuTarget}
							>
								<Group spacing="4xs">
									{activePersona?.avatar_url ? (
										<Avatar
											src={activePersona.avatar_url}
											size="xs"
											alt={`${activePersona.name} avatar`}
										/>
									) : (
										<Center p="4xs">
											<AIAvatar size="xs" speed="slow" iconSpeed="none" />
										</Center>
									)}
									{!isNil(activePersona) && (
										<Text size="sm" weight="bold">
											{isEmpty(activePersona?.name)
												? 'New persona'
												: activePersona?.name}
										</Text>
									)}
									{hasPersonas && (
										<Icon name="selector" color="icon/secondary/default" />
									)}
								</Group>
							</UnstyledButton>
						</Menu.Target>
						{hasPersonas && (
							<Menu.Dropdown>
								{sortedPersonas?.map((persona) => {
									const isActive = activePersona?.id === persona.id;
									const isDefaultPersona = persona.is_default;

									return (
										<Menu.Item
											key={persona.id}
											onClick={() => {
												// If clicking the default persona, clear the active persona ID
												// so it falls back to default
												if (isDefaultPersona) {
													setActiveAiPersonaId('');
												} else {
													setActiveAiPersonaId(persona?.id);
												}
											}}
											data-testid={
												isDefaultPersona ? 'ai-persona-default' : undefined
											}
											rightSection={
												isActive && (
													<Icon name="check" color="icon/secondary/default" />
												)
											}
											disabled={isActive}
											sx={{
												backgroundColor: isActive
													? theme.other.getColor('surface/tertiary/default')
													: undefined,
											}}
										>
											<Group spacing={theme.spacing.xs} noWrap>
												{persona.avatar_url ? (
													<Avatar
														src={persona.avatar_url}
														size="sm"
														alt={`${persona.name} avatar`}
													/>
												) : (
													<AIAvatar size="sm" speed="none" iconSpeed="none" />
												)}
												<Stack spacing={0} maw={300}>
													<Text size="sm" weight="bold">
														{isEmpty(persona.name)
															? 'New persona'
															: persona.name}
													</Text>
													<Text
														size="xs"
														color="text/secondary/default"
														truncate
													>
														{isDefaultPersona && !persona.description
															? 'Default persona'
															: persona.description}
													</Text>
												</Stack>
											</Group>
										</Menu.Item>
									);
								})}
							</Menu.Dropdown>
						)}
					</Menu>
				)}
				{!aiPersonas && <Button variant="tertiary">Secoda AI</Button>}
			</NavBarBreadcrumbWrapper>
			<NavBarRightSideWrapper>
				<Group spacing="3xs">
					{id && <AIShareButton promptId={id} />}
					<Tooltip
						label={
							<KeyboardShortcut keys={[MOD_KEY, 'Shift', 'O']}>
								New chat
							</KeyboardShortcut>
						}
					>
						<IconButton
							iconName="compose"
							variant="tertiary"
							onClick={handleNewConversation}
						/>
					</Tooltip>
				</Group>
			</NavBarRightSideWrapper>
			{!forceHideRightSidebarControl && (
				<NavBarSidesheetControlsWrapper>
					<IconButton
						iconName="layoutSidebar"
						variant="tertiary"
						tooltip={collapsed ? 'Show AI sidebar' : 'Hide AI sidebar'}
						highlight={!collapsed}
						onClick={() => setCollapsed(!collapsed)}
						data-testid="ai-history-toggle"
					/>
				</NavBarSidesheetControlsWrapper>
			)}
		</NavBarWrapper>
	);
}

const ObservedAIPageNavBar = observer(AIPageNavBar);
export default ObservedAIPageNavBar;
